import { Injectable } from '@angular/core';

import { ChatAuthService } from './chat-auth.service';
import { ChatLoggingService } from './chat-logging.service';
import { ChatUserDataService } from './chat-user-data.service';
import { ChatIntegrationStatus, ChatIntegrationType } from '../interfaces/chat-user-data';

const GRAPH_URL = 'https://graph.microsoft.com/v1.0';
// Graph wants pieces in multiples of 320 KiB; 10 x 320 KiB is its recommended size
const CHUNK_SIZE_BYTES = 10 * 320 * 1024;
const MAX_CHUNK_RETRIES = 5;
// A token this close to expiry could run out mid-call, so it counts as expired
const TOKEN_EXPIRY_MARGIN_MS = 60 * 1000;
// How long to wait for the host to hand over a fresh token before using the stored one
const HOST_DATA_TIMEOUT_MS = 10 * 1000;
// Characters OneDrive rejects in a file name
const INVALID_NAME_CHARS = /["*:<>?/\\|#%]/g;

export enum ChatOneDriveErrorCode {
  NotConnected = 'not_connected',
  Unauthorized = 'unauthorized',
  QuotaExceeded = 'quota_exceeded',
  SessionExpired = 'session_expired',
  Cancelled = 'cancelled',
  Failed = 'failed'
}

export class ChatOneDriveError extends Error {
  constructor(
    public readonly code: ChatOneDriveErrorCode,
    message?: string,
    public readonly status?: number
  ) {
    super(message || code);
    this.name = 'ChatOneDriveError';
  }
}

export interface ChatOneDriveSharedFile {
  itemId: string;
  // As stored - OneDrive may have renamed it to avoid a clash
  name: string;
  size: number;
  webUrl: string;
}

interface UploadSession {
  uploadUrl: string;
  expirationDateTime?: string;
}

/**
 * Shares a file through the logged-in user's OneDrive: uploads it to an upload session in chunks, straight from the
 * browser, then creates an organization view link to post in the chat. The Microsoft Graph token comes from the host
 * through ChatUserDataService (the user's msgraph integration) and is only ever sent to graph.microsoft.com - the
 * upload session URL is pre-authorized, so the chunks go without it.
 */
@Injectable({
  providedIn: 'root'
})
export class ChatOneDriveService {
  public folder = 'Chat Uploads';

  constructor(
    private chatAuthService: ChatAuthService,
    private chatUserDataService: ChatUserDataService,
    private chatLoggingService: ChatLoggingService
  ) {}

  public isConnected(): boolean {
    return !!this.getAccessToken();
  }

  /**
   * Uploads the file and returns its share link. Rejects with a ChatOneDriveError (Cancelled for any abort); if it
   * doesn't finish, the partial upload - or the uploaded file, when only the link failed - is discarded, so nothing
   * unshared is left in OneDrive.
   */
  public async shareFile(
    file: File,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal
  ): Promise<ChatOneDriveSharedFile> {
    try {
      const session = await this.createUploadSession(file, signal);
      const item = await this.uploadChunks(file, session, onProgress, signal);
      let webUrl: string;
      try {
        webUrl = await this.createLink(item.id, signal);
      } catch (ex) {
        this.discardItem(item.id);
        throw ex;
      }
      this.chatLoggingService.info({
        logMessage: 'chat-onedrive.service.shareFile(): shared',
        logParameters: [item.id, file.size]
      });
      return { itemId: item.id, name: item.name || file.name, size: item.size ?? file.size, webUrl };
    } catch (ex) {
      // An abort can surface as a raw AbortError (e.g. while a response body is read) - it's still a cancel
      this.throwIfAborted(signal);
      throw ex instanceof ChatOneDriveError ? ex : new ChatOneDriveError(ChatOneDriveErrorCode.Failed, ex?.message);
    }
  }

  private getAccessToken(): string | null {
    const integration = this.chatUserDataService
      .getData(this.chatAuthService.userId)
      ?.integrations?.find(i => i?.type === ChatIntegrationType.MSGRAPH);
    const accessToken = integration?.data?.accessToken;
    if (integration?.status !== ChatIntegrationStatus.CONNECTED || !accessToken) {
      return null;
    }
    const expiresAt = integration.data.expiresAt;
    if (expiresAt != null) {
      const expiresAtMs = typeof expiresAt === 'number' ? expiresAt : Date.parse(expiresAt);
      if (!isNaN(expiresAtMs) && expiresAtMs - TOKEN_EXPIRY_MARGIN_MS <= Date.now()) {
        return null;
      }
    }
    return accessToken;
  }

  // Asks the host for the user's current data before a Graph call, so a token it has refreshed since its last push
  // is used. Hosts without a handler only push, and a host that doesn't answer in time is skipped - either way the
  // stored token is used as is.
  private async getFreshAccessToken(signal?: AbortSignal): Promise<string> {
    try {
      await this.waitForHost(this.chatUserDataService.getUsersData([this.chatAuthService.userId]), signal);
    } catch (ex) {
      this.throwIfAborted(signal);
      this.chatLoggingService.debug({
        logMessage: 'chat-onedrive.service.getFreshAccessToken(): No fresh user data, using the stored token',
        logParameters: [ex?.message]
      });
    }
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      throw new ChatOneDriveError(ChatOneDriveErrorCode.NotConnected);
    }
    return accessToken;
  }

  private async createUploadSession(file: File, signal?: AbortSignal): Promise<UploadSession> {
    const accessToken = await this.getFreshAccessToken(signal);
    const path = [this.folder, this.toValidFileName(file.name)].map(encodeURIComponent).join('/');
    // OneDrive for Business rejects fileSize and name in this body, so only the conflict rule goes
    return this.graphRequest<UploadSession>(
      'POST',
      `${GRAPH_URL}/me/drive/root:/${path}:/createUploadSession`,
      accessToken,
      { item: { '@microsoft.graph.conflictBehavior': 'rename' } },
      signal
    );
  }

  private async createLink(itemId: string, signal?: AbortSignal): Promise<string> {
    // The upload can take long enough for the token from the start to expire
    const accessToken = await this.getFreshAccessToken(signal);
    const result = await this.graphRequest<{ link?: { webUrl?: string } }>(
      'POST',
      `${GRAPH_URL}/me/drive/items/${encodeURIComponent(itemId)}/createLink`,
      accessToken,
      { type: 'view', scope: 'organization' },
      signal
    );
    if (!result?.link?.webUrl) {
      throw new ChatOneDriveError(ChatOneDriveErrorCode.Failed, 'No share link returned');
    }
    return result.link.webUrl;
  }

  private async uploadChunks(
    file: File,
    session: UploadSession,
    onProgress?: (fraction: number) => void,
    signal?: AbortSignal
  ): Promise<{ id: string; name?: string; size?: number }> {
    let start = 0;
    let retries = 0;
    try {
      while (start < file.size) {
        this.throwIfAborted(signal);
        const end = Math.min(start + CHUNK_SIZE_BYTES, file.size);
        let response: Response;
        try {
          // No Authorization header: the URL carries its own permission, and Graph can reject a request that adds one
          response = await fetch(session.uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Range': `bytes ${start}-${end - 1}/${file.size}` },
            body: file.slice(start, end),
            signal
          });
        } catch (ex) {
          // Network drop: wait, then ask the session where to carry on rather than starting over
          this.throwIfAborted(signal);
          if (++retries > MAX_CHUNK_RETRIES) {
            throw new ChatOneDriveError(ChatOneDriveErrorCode.Failed, ex?.message);
          }
          await this.delay(Math.min(30000, 1000 * 2 ** retries), signal);
          start = await this.getNextExpectedStart(session, signal);
          continue;
        }
        if (response.status === 200 || response.status === 201) {
          onProgress?.(1);
          return await response.json();
        }
        if (response.status === 202) {
          const body = await response.json();
          start = this.parseNextExpectedStart(body?.nextExpectedRanges, end);
          retries = 0;
          onProgress?.(start / file.size);
          continue;
        }
        // 5xx: Graph asks callers to resume; anything else is final
        if (response.status >= 500 && response.status !== 507 && ++retries <= MAX_CHUNK_RETRIES) {
          await this.delay(Math.min(30000, 1000 * 2 ** retries), signal);
          start = await this.getNextExpectedStart(session, signal);
          continue;
        }
        throw await this.toError(response, true);
      }
      throw new ChatOneDriveError(ChatOneDriveErrorCode.Failed, 'Upload ended without a file');
    } catch (ex) {
      // Any abort, including one while a response body was read. Discard the partial upload; it would expire anyway
      if (signal?.aborted || (ex instanceof ChatOneDriveError && ex.code === ChatOneDriveErrorCode.Cancelled)) {
        fetch(session.uploadUrl, { method: 'DELETE' }).catch(() => undefined);
      }
      throw ex;
    }
  }

  // Best effort, and without the signal: it runs after a cancel, when that signal is already aborted
  private discardItem(itemId: string) {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return;
    }
    fetch(`${GRAPH_URL}/me/drive/items/${encodeURIComponent(itemId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` }
    }).catch(() => undefined);
  }

  private async getNextExpectedStart(session: UploadSession, signal?: AbortSignal): Promise<number> {
    let response: Response;
    try {
      response = await fetch(session.uploadUrl, { signal });
    } catch (ex) {
      this.throwIfAborted(signal);
      throw new ChatOneDriveError(ChatOneDriveErrorCode.Failed, ex?.message);
    }
    if (!response.ok) {
      throw await this.toError(response, true);
    }
    const body = await response.json();
    return this.parseNextExpectedStart(body?.nextExpectedRanges, 0);
  }

  // nextExpectedRanges looks like ["26214400-"] or ["0-1023", "2048-"]; the first range's start is next
  private parseNextExpectedStart(ranges: string[], fallback: number): number {
    const start = parseInt(String(ranges?.[0] ?? '').split('-')[0], 10);
    return isNaN(start) ? fallback : start;
  }

  private async graphRequest<T>(
    method: string,
    url: string,
    accessToken: string,
    body: unknown,
    signal?: AbortSignal
  ): Promise<T> {
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal
      });
    } catch (ex) {
      this.throwIfAborted(signal);
      throw new ChatOneDriveError(ChatOneDriveErrorCode.Failed, ex?.message);
    }
    if (!response.ok) {
      throw await this.toError(response);
    }
    return response.json();
  }

  // forUploadSession: a 404 from the upload URL means the session expired or was removed
  private async toError(response: Response, forUploadSession = false): Promise<ChatOneDriveError> {
    let graphCode: string;
    let graphMessage: string;
    try {
      const body = await response.json();
      graphCode = body?.error?.code;
      graphMessage = body?.error?.message;
    } catch {
      // Not JSON - the status is enough
    }
    this.chatLoggingService.warn({
      logMessage: 'chat-onedrive.service: Graph request failed',
      logParameters: [response.status, graphCode, graphMessage]
    });
    if (response.status === 401) {
      return new ChatOneDriveError(ChatOneDriveErrorCode.Unauthorized, graphMessage, response.status);
    }
    if (response.status === 404 && forUploadSession) {
      return new ChatOneDriveError(ChatOneDriveErrorCode.SessionExpired, graphMessage, response.status);
    }
    if (response.status === 507 || graphCode === 'quotaLimitReached') {
      return new ChatOneDriveError(ChatOneDriveErrorCode.QuotaExceeded, graphMessage, response.status);
    }
    return new ChatOneDriveError(ChatOneDriveErrorCode.Failed, graphMessage || graphCode, response.status);
  }

  private toValidFileName(name: string): string {
    const valid = (name || '').replace(INVALID_NAME_CHARS, '_').trim();
    return valid || 'file';
  }

  private throwIfAborted(signal?: AbortSignal) {
    if (signal?.aborted) {
      throw new ChatOneDriveError(ChatOneDriveErrorCode.Cancelled);
    }
  }

  // Stops waiting on the host's handler after HOST_DATA_TIMEOUT_MS, or at once on cancel
  private waitForHost<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
    this.throwIfAborted(signal);
    return new Promise((resolve, reject) => {
      const settle = (finish: () => void) => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        finish();
      };
      const onAbort = () => settle(() => reject(new ChatOneDriveError(ChatOneDriveErrorCode.Cancelled)));
      const timer = setTimeout(
        () => settle(() => reject(new Error('The host did not answer in time'))),
        HOST_DATA_TIMEOUT_MS
      );
      signal?.addEventListener('abort', onAbort, { once: true });
      promise.then(
        value => settle(() => resolve(value)),
        ex => settle(() => reject(ex))
      );
    });
  }

  private delay(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, ms);
      signal?.addEventListener(
        'abort',
        () => {
          clearTimeout(timer);
          reject(new ChatOneDriveError(ChatOneDriveErrorCode.Cancelled));
        },
        { once: true }
      );
    });
  }
}
